import { Accordion } from "components/common/Accordion";
import { HeadingTag } from "components/common/Heading";
import { Heading } from "components/common/Heading";
import { VisibleSection } from "components/common/VisibleSection";
import { AssertionAnswers } from "domain/assertions";
import { Button, Col } from "reactstrap";
import { brand } from "services/envService";
import { AssertionQuestion } from "./AssertionQuestion";
import {
  AUTHORISED_TO_ACT,
  CANNOT_USE_ONLINE_JOURNEY,
  BUSINESS_CEASED_TRADING,
  CONSENT_TO_USE_PAYG,
  REQUEST_REPAIMENT_HOLIDAY,
  EXTEND_REMAINING_TERM,
  ASSERTION_HEADING,
  ASSERTION_SUBHEADING1_PARA1,
  ASSERTION_SUBHEADING1_PARA2,
  ASSERTION_SUBHEADING1_PARA3,
  ASSERTION_SUBHEADING2,
  ASSERTION_SUBHEADING3,
  WEBSITE_URLS,
  CEASED_TRADING_ASSERTION_YES_LABEL,
  CEASED_TRADING_ASSERTION_NO_LABEL,
  ASSERTION_SCREEN_QUESTION,
} from "./Assertions.const";
import { MissedRepaymentText } from "./MissedRepaymentText";
import {
  ExternTermAssertionText,
  RepaymentAssertionText,
} from "./RepaymentAssertion";

export type AssertionsProps = {
  questions: AssertionAnswers;
  onChange: (answers: AssertionAnswers) => void;
  onReady: () => void;
  onWrongAnswer: (answers: AssertionAnswers) => void;
  disabled?: boolean;
};

const BRAND = brand();

const Assertions = ({
  disabled,
  questions,
  onChange,
  onReady,
  onWrongAnswer,
}: AssertionsProps) => {
  const [q0, q1, q2, q3] = questions.answers;
  const q1visible = q0.answer === q0.expected;
  const q2visible = q1.answer === q1.expected;
  const q3visible = q2.answer === q2.expected;

  const ready = questions.answers.every(
    ({ answer, expected }) => answer === expected
  );

  const handleAnswer = (idx: number) => (answer: boolean) => {
    const newAnswers = questions.answers.map((answ, i) =>
      idx === i ? { ...answ, answer } : answ
    );
    if (newAnswers[idx].answer === newAnswers[idx].expected) {
      onChange({ ...questions, answers: newAnswers });
    } else {
      const clearedAnswers = newAnswers.map((a, i) =>
        i <= idx ? a : { ...a, answer: null }
      );
      onWrongAnswer({ ...questions, answers: clearedAnswers });
    }
  };

  return (
    <Col className="narrow-container assertions-page">
      <div className="content-wrapper questions-screen">
        <div>
          <Heading
            tag={HeadingTag.h1}
            size={1}
            className="zb-heading1 screen-header"
            title={ASSERTION_HEADING}
          />
          <div>{ASSERTION_SUBHEADING1_PARA1}</div>
          <div className="newline">{ASSERTION_SUBHEADING1_PARA2}</div>
          <div className="newline">{ASSERTION_SUBHEADING1_PARA3}</div>
          <MissedRepaymentText />
          <div>
            <span className="newline">{ASSERTION_SUBHEADING2}</span>
            <span>
              <a rel="noreferrer" target="_blank" href={WEBSITE_URLS[BRAND]}>
                please visit our website
              </a>
            </span>
          </div>
          <div className="newline">{ASSERTION_SUBHEADING3}</div>
          <div className="assertion-content newline">
            <Accordion header={REQUEST_REPAIMENT_HOLIDAY}>
              <RepaymentAssertionText />
            </Accordion>

            <Accordion header={EXTEND_REMAINING_TERM}>
              <ExternTermAssertionText />
            </Accordion>
            <Heading
              tag={HeadingTag.h2}
              size={2}
              className="zb-heading2"
              title={ASSERTION_SCREEN_QUESTION}
            />
            <div className="questions-block">
              <VisibleSection visible>
                <div className="question-container">
                  <AssertionQuestion
                    name={q0.type}
                    value={q0.answer}
                    disabled={disabled}
                    onChange={handleAnswer(0)}
                    confirmValue={!q0.expected}
                  >
                    <p className="questions-screen-question">
                      {CANNOT_USE_ONLINE_JOURNEY}
                    </p>
                  </AssertionQuestion>
                </div>
              </VisibleSection>

              <VisibleSection visible={q1visible}>
                <div className="question-container">
                  <hr className="top-header-line" />
                  <AssertionQuestion
                    name={q1.type}
                    value={q1.answer}
                    disabled={disabled}
                    onChange={handleAnswer(1)}
                    confirmValue={!q1.expected}
                  >
                    <p className="questions-screen-question">
                      {AUTHORISED_TO_ACT}
                    </p>
                  </AssertionQuestion>
                </div>
              </VisibleSection>

              <VisibleSection visible={q2visible}>
                <div className="question-container">
                  <hr className="top-header-line" />
                  <AssertionQuestion
                    name={q2.type}
                    value={q2.answer}
                    disabled={disabled}
                    onChange={handleAnswer(2)}
                    confirmValue={!q2.expected}
                    noLabel="No, I'd prefer to speak with someone about my situation"
                  >
                    <p className="questions-screen-question">
                      {CONSENT_TO_USE_PAYG}
                    </p>
                  </AssertionQuestion>
                </div>
              </VisibleSection>

              <VisibleSection visible={q3visible}>
                <div className="question-container">
                  <hr className="top-header-line" />
                  <AssertionQuestion
                    name={q3.type}
                    value={q3.answer}
                    disabled={disabled}
                    onChange={handleAnswer(3)}
                    confirmValue={!q3.expected}
                    yesLabel={CEASED_TRADING_ASSERTION_YES_LABEL}
                    noLabel={CEASED_TRADING_ASSERTION_NO_LABEL}
                  >
                    <p className="questions-screen-question">
                      {BUSINESS_CEASED_TRADING}
                    </p>
                  </AssertionQuestion>
                </div>
              </VisibleSection>
            </div>
          </div>
        </div>
        <div className=" button-end">
          <Button color="primary" size="lg" disabled={!ready} onClick={onReady}>
            Next
          </Button>
        </div>
      </div>
    </Col>
  );
};

export { Assertions };
